/** tm/legal/LegalHubPage.tsx — 「📚 법령·판례·뉴스」 — 정부지원사업과 같은 3열(필터 176 · 목록 280 · 개요/상세). 따로 도구 줄 없이 3열 첫 줄 오른쪽에 수집 상태·새 소식·설정.
 *  모바일: 「필터」 시트(여러 개 고른 뒤 「적용」) + 한 줄 설명 + 첫 화면은 새 소식(있으면) 또는 전체 주제 개요 · 상세는 겹침 화면. 뒤로 가기는 겹 화면부터 닫는다. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { C } from '../components/ui';
import type { TMStore } from '../hooks';
import { markRead } from './legalApi';
import { btn, TABS } from './legalTheme';
import { LegalGlobalStyle, useToast, useWindowWidth, MOBILE_BP, Banner } from './legalUi';
import { useLegalFeed, type RowPatch } from './useLegalFeed';
import { useLegalHubData } from './useLegalHubData';
import { useLegalView } from './useLegalView';
import { useBackLayer } from './useBackLayer';
import { useReadAll } from './useReadAll';
import type { LegalAlertsState } from './useLegalAlerts';
import { kindOf, type AlertEntry } from './alertUtil';
import LegalToolbar, { LegalErrorBar, MobileBar } from './LegalToolbar';
import LegalAlertsPanel from './LegalAlertsPanel';
import LegalFilterColumn, { type FilterValue } from './LegalFilterColumn';
import LegalFilterSheet, { FilterButton, MobileIntro } from './LegalFilterSheet';
import LegalFeedList from './LegalFeedList';
import LegalOverview from './LegalOverview';
import LegalDetailPane from './LegalDetailPane';
import LegalSettingsModal from './LegalSettingsModal';
import type { AlertTab, FeedRow, LegalNavParams, Selection } from './legalTypes';

interface Props {
  store: TMStore; isSuper: boolean; alerts: LegalAlertsState; navParams?: LegalNavParams; userId?: string;
  /** 업체 연결 숨김 — 올댓벤처스 어드민에 끼워 쓸 때 (2026-10-03) */
  noCompanies?: boolean;
}

export default function LegalHubPage({ store, isSuper, alerts, navParams, userId = '', noCompanies = false }: Props) {
  const mobile = useWindowWidth() < MOBILE_BP;
  const [toastNode, toast] = useToast();
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draft, setDraft] = useState<FilterValue | null>(null); // 모바일 필터 시트 (열려 있으면 임시 선택)
  const [mobileList, setMobileList] = useState(false);
  const { refresh: refreshAlerts } = alerts;
  const data = useLegalHubData(s => {
    feed.reload(); data.refreshTopics(); refreshAlerts();
    const r = s?.lastRun;
    toast(r?.status === 'error' ? `수집 실패: ${r.error || '알 수 없는 오류'}` : `수집을 마쳤습니다${r ? ` · 새 글 ${r.new_count}건` : ''}`, r?.status === 'error' ? 'error' : 'ok');
  });
  const { topics } = data;
  const v = useLegalView(userId, navParams, topics);
  const { topic, starred, tab, lawKind, sub, q, onlyNew, sel } = v;
  const value: FilterValue = { topic: topic || '', starred, onlyNew, tab, lawKind };

  const feedQuery = useMemo(() => ({ topic: topic || '', tab, sub, q, onlyNew, starred, lawKind }), [topic, tab, sub, q, onlyNew, starred, lawKind]);
  const [ready, setReady] = useState(topic !== null);
  useEffect(() => { if (topic !== null || !data.topicsLoading) setReady(true); }, [topic, data.topicsLoading]);
  const feed = useLegalFeed(feedQuery, ready);
  const current = starred ? null : topics.find(t => t.label === topic) || null;
  const quietLabels = new Set(topics.filter(t => t.alert === false).map(t => t.label));
  const alertableIn = (labels: string[]) => labels.some(l => !quietLabels.has(l));

  // 첫 화면(데스크톱·모바일 같음): 새 소식이 있으면 「새 소식」, 없으면 전체 주제 개요 (뉴스 흐름부터 보여 주지 않는다)
  const firstDone = useRef(false);
  const alertsLoaded = alerts.data.latest_first_seen_at !== undefined;
  useEffect(() => {
    if (firstDone.current || !topics.length || !alertsLoaded) return;
    firstDone.current = true;
    if (navParams?.itemId || navParams?.topic !== undefined) return;
    v.applyFilter({ topic: '', starred: false, onlyNew: alerts.total > 0, tab: 'all', lawKind: '' });
  }, [topics.length, alertsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // 다른 화면(팝업·대시보드·PC 알림)에서 글을 열고 들어오면 읽음 처리
  useEffect(() => {
    if (!navParams?.itemId) return;
    markRead(navParams.kind || 'legal', navParams.itemId).then(() => { refreshAlerts(); data.refreshTopics(); }, () => undefined);
  }, [navParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // 뒤로 가기 → 겹 화면부터 닫기
  useBackLayer(mobile && !!sel, () => v.setSel(null));
  useBackLayer(!!draft, () => setDraft(null));
  useBackLayer(alertsOpen, () => setAlertsOpen(false));

  /** 목록에서 열기 — 읽음 표시를 먼저 반영하고(낙관적) 서버에 보낸다 */
  const openRow = (r: FeedRow) => {
    v.setSel({ kind: r.kind, id: r.id });
    if (!r.unread) return;
    feed.patch(r.kind, r.id, { unread: false });
    if (r.kind === 'news') { if (r.is_new) data.decNews(r.topics); markRead('news', r.id).catch(() => undefined); return; }
    const alertable = r.is_new && alertableIn(r.topics) && !(r.kind === 'legal' && r.meta?.minor);
    const t: AlertTab = r.kind === 'bill' || r.kind === 'notice' || r.kind === 'cabinet' ? r.kind : r.tab === 'news' || r.tab === 'bill' || r.tab === 'notice' || r.tab === 'cabinet' ? 'law' : r.tab;
    if (alertable) data.decAlerts(r.topics, t);
    markRead(r.kind, r.id).then(() => { if (alertable) refreshAlerts(); }, () => feed.patch(r.kind, r.id, { unread: true }));
  };
  const openAlert = (e: AlertEntry) => {
    setAlertsOpen(false);
    const kind = kindOf(e.key);
    v.jumpTo(e.it.topic, { kind, id: e.it.id });
    feed.patch(kind, e.it.id, { unread: false });
    markRead(kind, e.it.id).then(() => { refreshAlerts(); data.refreshTopics(); }, () => undefined);
  };
  /** 「진행 단계」에서 이어진 글 열기 — 읽음 처리까지 (목록에서 여는 것과 같게) */
  const openLinked = (s: NonNullable<Selection>) => {
    v.setSel(s);
    feed.patch(s.kind, s.id, { unread: false });
    markRead(s.kind, s.id).then(() => { refreshAlerts(); data.refreshTopics(); }, () => undefined);
  };
  const readAll = useReadAll(alerts, toast, () => { setAlertsOpen(false); data.refreshTopics(); feed.reload(); });
  const runNow = async () => { const r = await data.run(); toast(r.message, r.ok ? 'ok' : 'error'); };
  const patchSel = (p: RowPatch) => {
    if (!sel) return;
    feed.patch(sel.kind, sel.id, p);
    if (p.unread) { refreshAlerts(); data.refreshTopics(); }
  };
  const apply = (f: FilterValue) => { v.applyFilter(f); setMobileList(false); };
  /** 「새 소식」·「★ 별표」 보기에서 검색하면 전체에서 찾는다 (검색이 0건으로 막히지 않게) */
  const searchAll = (text: string) => { if (text.trim() && (onlyNew || starred)) v.applyFilter({ ...value, onlyNew: false, starred: false }); v.setQ(text); };

  const toolbar = (
    <span style={{ position: 'relative', display: 'inline-flex', zIndex: 5 }}>
      <LegalToolbar summary={data.summary} running={data.running} isSuper={isSuper} mobile={mobile} alertsTotal={alerts.total} alertsOpen={alertsOpen}
        onBell={() => { setAlertsOpen(o => !o); if (!alertsOpen) refreshAlerts(); }} onSettings={() => setSettingsOpen(true)} onRun={runNow} />
      {alertsOpen && <LegalAlertsPanel data={alerts.data} mobile={mobile} onOpen={openAlert} onReadAll={readAll} onClose={() => setAlertsOpen(false)} onToast={toast} />}
    </span>
  );
  const filterLabel = [starred ? '★ 별표' : onlyNew && !topic ? '새 소식' : topic || '전체 주제', tab !== 'all' ? TABS.find(t => t.id === tab)?.label : '', lawKind].filter(Boolean).join(' · ');
  const overview = (head?: boolean, footer?: boolean) => (
    <LegalOverview topic={current} topics={topics} tab={tab} starred={starred} onlyNew={onlyNew && !topic} alertsTotal={alerts.total} mobile={mobile}
      head={head ? toolbar : undefined} alerts={alerts.data} onReadAll={() => { readAll(); }} onTab={t => apply({ ...value, tab: t })} onTopic={l => apply({ ...value, topic: l, starred: false, onlyNew: false })} onOpen={openRow}
      onSelect={(kind, id) => v.setSel({ kind, id })}
      footer={footer ? <button type="button" className="lw-f" onClick={() => setMobileList(true)} style={{ ...btn(), width: '100%', justifyContent: 'center' }}>전체 글 목록 보기</button> : undefined} />
  );
  const mobileOverview = mobile && !mobileList && topic === '' && !onlyNew && !starred && tab === 'all' && !q && !sub;
  const detail = sel ? <LegalDetailPane sel={sel} mobile={mobile} companies={noCompanies ? null : store.companies} head={mobile ? undefined : toolbar}
    onBack={() => v.setSel(null)} onToast={toast} onPatch={patchSel} onOpen={openLinked} /> : overview(true);
  const billBanner = tab === 'bill' && data.bills && !data.bills.configured
    ? <div style={{ margin: '0 12px 8px' }}><Banner kind="warn">새 법안 수집이 꺼져 있습니다 (서버 ASSEMBLY_API_KEY 필요). 모아 둔 법안만 보입니다.</Banner></div> : undefined;

  return (
    <div className="lw-root" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <LegalGlobalStyle />
      {mobile && <MobileBar left={<FilterButton label={filterLabel} onClick={() => setDraft(value)} />}>{toolbar}</MobileBar>}
      <LegalErrorBar summary={data.summary} />
      {data.topicsError && <div role="alert" style={{ padding: '6px 16px', background: C.redLt, color: C.red, fontSize: 13 }}>{data.topicsError}</div>}
      <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: mobile ? 'auto' : 'hidden', flexDirection: mobile ? 'column' : 'row' }}>
        {!mobile && <div style={{ width: 176, flexShrink: 0, borderRight: `1px solid ${C.border}`, overflowY: 'auto', padding: '14px 12px' }}>
          <LegalFilterColumn topics={topics} value={value} alertsTotal={alerts.total} loading={data.topicsLoading} facets={feed.facets} onChange={apply} />
        </div>}
        {mobile && <MobileIntro userId={userId} />}
        {mobileOverview ? <div style={{ padding: '14px 16px 24px' }}>{overview(false, true)}</div> : (
          <LegalFeedList feed={feed} tab={tab} q={q} onlyNew={onlyNew} sel={sel} mobile={mobile} starred={starred} banner={billBanner} lawKind={lawKind}
            subs={current?.subtopics || []} subCounts={current?.subCounts} sub={sub} onQ={searchAll} onSub={v.setSub} onSelect={openRow}
            unreadTotal={alerts.data ? alerts.data.total : 0} onReadAll={readAll} />
        )}
        {!mobile && <article aria-label={sel ? '상세' : '개요'} style={{ flex: 1, overflowY: 'auto', padding: '14px 20px', minWidth: 0 }}>{detail}</article>}
      </div>
      {mobile && sel && (
        <article aria-label="상세" role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, zIndex: 80, background: C.white, overflowY: 'auto', padding: '14px 16px 32px' }}>{detail}</article>
      )}
      {mobile && draft && (
        <LegalFilterSheet onClose={() => setDraft(null)} onApply={() => { apply(draft); setDraft(null); }} extra={isSuper ? <>
          <button type="button" className="lw-f" onClick={() => { setDraft(null); setSettingsOpen(true); }} style={{ ...btn(), flex: 1, justifyContent: 'center' }}>주제·알림 설정</button>
          <button type="button" className="lw-f" disabled={data.running} onClick={() => { setDraft(null); runNow(); }} style={{ ...btn(), flex: 1, justifyContent: 'center' }}>{data.running ? '수집 중' : '지금 수집'}</button>
        </> : undefined}>
          <LegalFilterColumn topics={topics} value={draft} alertsTotal={alerts.total} loading={data.topicsLoading} mobile onChange={setDraft} />
        </LegalFilterSheet>
      )}
      {settingsOpen && <LegalSettingsModal mobile={mobile} onToast={toast} onClose={() => { setSettingsOpen(false); data.refreshTopics(); data.refreshSummary(); feed.reload(); }} />}
      {toastNode}
    </div>
  );
}
